/**
 * Widget Disponibilités Propriétaire
 * Phase 3.2 - Intégration avec le système unifié
 */

import React from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import {
  CalendarMonth as CalendarIcon,
  CheckCircle as AvailableIcon,
  Block as BlockedIcon,
  Visibility as ViewIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

import { DashboardWidget, StatusIndicator } from '../DashboardBase';
import { 
  formatPercentage,
  PERIOD_LABELS 
} from '../../../constants/dashboardConstants';

const OwnerAvailabilityWidget = ({ data, loading, period, onRefresh }) => {
  const navigate = useNavigate();

  if (!data && !loading) {
    return (
      <DashboardWidget
        title="Disponibilités"
        loading={loading}
        onRefresh={onRefresh}
      >
        <Typography color="text.secondary">
          Aucune donnée de disponibilités disponible
        </Typography>
      </DashboardWidget>
    );
  }

  const {
    available_days = 0,
    booked_days = 0,
    blocked_days = 0,
    total_days = 0,
    occupancy_rate = 0,
    availability_rate = 0,
    by_listing = []
  } = data || {};

  const handleViewCalendar = () => {
    navigate('/owner/calendar');
  };

  const handleBlockDates = () => {
    navigate('/owner/calendar?action=block');
  };

  // Configuration des statuts de disponibilité
  const availabilityStats = [
    {
      label: 'Jours disponibles',
      value: available_days,
      color: 'success',
      icon: <AvailableIcon />
    },
    {
      label: 'Jours réservés',
      value: booked_days,
      color: 'primary',
      icon: <CalendarIcon />
    },
    {
      label: 'Jours bloqués',
      value: blocked_days,
      color: 'error',
      icon: <BlockedIcon />
    }
  ];

  return (
    <DashboardWidget
      title={`Disponibilités - ${PERIOD_LABELS[period]}`}
      loading={loading}
      onRefresh={onRefresh}
      actions={
        <Box display="flex" gap={1}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<ViewIcon />}
            onClick={handleViewCalendar}
          >
            Voir calendrier
          </Button>
          <Button
            variant="contained"
            size="small"
            startIcon={<BlockedIcon />}
            onClick={handleBlockDates}
            color="warning"
          >
            Bloquer dates
          </Button>
        </Box>
      }
      height={600}
    >
      {/* Résumé général */}
      <Grid container spacing={3} mb={3}>
        {/* Statistiques principales */}
        <Grid item xs={12} md={8}>
          <Typography variant="h6" gutterBottom>
            Vue d'ensemble
          </Typography>
          
          <Grid container spacing={2}>
            {availabilityStats.map((stat, index) => (
              <Grid item xs={4} key={index}>
                <Paper 
                  sx={{ 
                    p: 2, 
                    textAlign: 'center',
                    bgcolor: `${stat.color}.50`,
                    border: 1,
                    borderColor: `${stat.color}.200`
                  }}
                >
                  <Box display="flex" justifyContent="center" mb={1}>
                    <Box sx={{ color: `${stat.color}.main` }}>
                      {stat.icon}
                    </Box>
                  </Box>
                  <Typography variant="h4" color={`${stat.color}.main`} fontWeight="bold">
                    {stat.value}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {stat.label}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Grid>

        {/* Taux globaux */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2, height: '100%' }}>
            <Typography variant="h6" gutterBottom>
              Taux de performance
            </Typography>
            
            <Box mb={2}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <Typography variant="body2" color="text.secondary">
                  Taux d'occupation
                </Typography>
                <Chip
                  label={formatPercentage(occupancy_rate)}
                  color={occupancy_rate > 70 ? 'success' : occupancy_rate > 40 ? 'warning' : 'error'}
                  size="small"
                />
              </Box>
              
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="body2" color="text.secondary">
                  Taux de disponibilité
                </Typography>
                <Chip
                  label={formatPercentage(availability_rate)}
                  color="info"
                  size="small"
                />
              </Box>
            </Box>

            <Typography variant="caption" color="text.secondary">
              Sur {total_days} jours au total
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Détail par annonce */}
      {by_listing && by_listing.length > 0 && (
        <Box>
          <Typography variant="h6" gutterBottom>
            Détail par annonce
          </Typography>
          
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Annonce</TableCell>
                  <TableCell align="center">Disponible</TableCell>
                  <TableCell align="center">Réservé</TableCell>
                  <TableCell align="center">Bloqué</TableCell>
                  <TableCell align="center">Taux occupation</TableCell>
                  <TableCell align="center">Statut</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {by_listing.map((listing) => {
                  const listingOccupancy = listing.total_days > 0 
                    ? Math.round((listing.booked_days / listing.total_days) * 100)
                    : 0;
                    
                  return (
                    <TableRow key={listing.id}>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {listing.title}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2" color="success.main">
                          {listing.available_days}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2" color="primary.main">
                          {listing.booked_days}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2" color="error.main">
                          {listing.blocked_days}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={`${listingOccupancy}%`}
                          color={listingOccupancy > 70 ? 'success' : listingOccupancy > 40 ? 'warning' : 'error'}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell align="center">
                        <StatusIndicator
                          status={listing.available_days > 0 ? 'disponible' : 'bloquee'}
                          label={listing.available_days > 0 ? 'Actif' : 'Bloqué'}
                          variant="chip"
                          size="small"
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* Conseils contextuels */}
      <Box mt={3}>
        {occupancy_rate < 30 && (
          <Box p={2} bgcolor="info.50" borderRadius={2} mb={2}>
            <Typography variant="caption" color="info.dark">
              💡 <strong>Conseil :</strong> Votre taux d'occupation est faible. 
              Considérez ajuster vos prix ou améliorer vos annonces pour attirer plus de réservations.
            </Typography>
          </Box>
        )}
        
        {availability_rate < 50 && (
          <Box p={2} bgcolor="warning.50" borderRadius={2}>
            <Typography variant="caption" color="warning.dark">
              ⚠️ <strong>Attention :</strong> Beaucoup de vos dates sont bloquées. 
              Assurez-vous que c'est intentionnel ou libérez des créneaux pour augmenter vos revenus.
            </Typography>
          </Box>
        )}
      </Box>
    </DashboardWidget>
  );
};

export default OwnerAvailabilityWidget;
